import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { chartTooltipProps } from '@/lib/chartTheme';
import { revenueSeriesColors } from '@/styles/theme';
import { centsToEuros, formatCentsForDisplay } from '@/utils/money';
import { dateFnsLocale, formatNumber, intlLocale } from '@/utils/locale';
import type { RevenueCategory, RevenueData, RevenueSummary } from '@/hooks';

interface RevenueChartProps {
  summary?: RevenueSummary | undefined;
  /** The window day by day, oldest first (`date` is a `YYYY-MM-DD` key). */
  daily?: RevenueData[] | undefined;
}

type SeriesKey = 'total' | RevenueCategory;

/** Drawn back to front: the total behind, the categories over it. */
const SERIES: readonly { key: SeriesKey; labelKey: string }[] = [
  { key: 'total', labelKey: 'revenueSeries.total' },
  { key: 'company', labelKey: 'revenueSeries.company' },
  { key: 'consumer', labelKey: 'revenueSeries.consumer' },
  { key: 'cashCard', labelKey: 'revenueSeries.cashCard' },
];

/** Euros per series for the axis, exact cents (`<key>Cents`) for the tooltip. */
type ChartRow = Record<string, number | string>;

interface DailyRow {
  label: string;
  fullDate: string;
  revenue: number;
  revenueCents: number;
}

/** A `YYYY-MM-DD` key as a local date, so no time zone shifts the day. */
function dateFromKey(key: string): Date {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

const euroTick = (value: number) => `${formatNumber(value)}\u00A0€`;

/**
 * Invoiced net revenue (#3906): the last `windowDays` days as the headline
 * total and day by day (#4010), and the current year to date month by month,
 * split by who paid.
 */
export function RevenueChart({ summary, daily }: RevenueChartProps) {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();

  const colors: Record<SeriesKey, string> = {
    ...revenueSeriesColors[theme.palette.mode],
    total: theme.palette.text.disabled,
  };

  // The current year up to this month — the months ahead are not zero revenue.
  const months = useMemo(() => {
    const currentMonth = summary?.asOf.slice(0, 7) ?? '';
    return (summary?.year ?? []).filter((m) => m.month <= currentMonth);
  }, [summary]);
  const yearCents = useMemo(() => months.reduce((sum, m) => sum + m.revenueCents, 0), [months]);
  const chartData = useMemo<ChartRow[]>(
    () =>
      months.map((m) => {
        const [year, month] = m.month.split('-').map(Number);
        const cents: Record<SeriesKey, number> = { total: m.revenueCents, ...m.byCategory };
        const row: ChartRow = {
          label: format(new Date(year ?? 0, (month ?? 1) - 1, 1), 'LLL', {
            locale: dateFnsLocale(),
          }),
        };
        for (const { key } of SERIES) {
          row[key] = centsToEuros(cents[key]);
          row[`${key}Cents`] = cents[key];
        }
        return row;
      }),
    [months]
  );
  const year = summary?.asOf.slice(0, 4) ?? '';

  const dailyData = useMemo<DailyRow[]>(
    () =>
      (daily ?? []).map((d) => {
        const date = dateFromKey(d.date);
        return {
          label: date.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'numeric' }),
          fullDate: date.toLocaleDateString(intlLocale()),
          revenue: centsToEuros(d.revenueCents),
          revenueCents: d.revenueCents,
        };
      }),
    [daily]
  );

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('revenueOverview')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('lastNDays', { count: summary?.windowDays ?? 14 })}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" fontWeight={600} color="success.main">
              {formatCentsForDisplay(summary?.totalCents ?? 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('revenueNetOfVat')}
            </Typography>
          </Box>
        </Box>

        {dailyData.length > 0 && (
          <Box sx={{ height: 160, mb: 3 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.palette.divider}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={8}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                  width={80}
                  tickFormatter={euroTick}
                />
                <Tooltip
                  {...chartTooltipProps(theme)}
                  cursor={{ fill: theme.palette.action.hover }}
                  labelFormatter={(_label: string, payload: { payload?: DailyRow }[]) =>
                    payload[0]?.payload?.fullDate ?? ''
                  }
                  formatter={(
                    _value: number,
                    _name: string,
                    item: { payload?: DailyRow }
                  ) => [formatCentsForDisplay(item.payload?.revenueCents ?? 0), t('revenue')]}
                />
                <Bar
                  dataKey="revenue"
                  name={t('revenue')}
                  fill={theme.palette.success.main}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">{t('revenueYear', { year })}</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {formatCentsForDisplay(yearCents)}
          </Typography>
        </Box>

        {/*
          Plain markup rather than Recharts' <Legend>: inside ResponsiveContainer
          the legend's own height feeds back into the plot's size.
        */}
        <Box
          component="ul"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, listStyle: 'none', p: 0, m: 0, mb: 1 }}
        >
          {SERIES.map(({ key, labelKey }) => (
            <Box component="li" key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                aria-hidden
                sx={{ width: 16, height: 2, borderRadius: 1, bgcolor: colors[key] }}
              />
              <Typography variant="caption" color="text.secondary">
                {t(labelKey)}
              </Typography>
            </Box>
          ))}
        </Box>

        {chartData.length > 0 && (
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.palette.divider}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={false}
                  width={80}
                  tickFormatter={euroTick}
                />
                <Tooltip
                  {...chartTooltipProps(theme)}
                  formatter={(
                    _value: number,
                    name: string,
                    item: { dataKey?: string | number; payload?: ChartRow }
                  ) => [
                    formatCentsForDisplay(
                      Number(item.payload?.[`${String(item.dataKey)}Cents`] ?? 0)
                    ),
                    name,
                  ]}
                />
                {SERIES.map(({ key, labelKey }) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={t(labelKey)}
                    stroke={colors[key]}
                    strokeWidth={2}
                    fill={colors[key]}
                    fillOpacity={key === 'total' ? 0.15 : 0.2}
                    dot={{ r: 3, strokeWidth: 2, fill: theme.palette.background.paper }}
                    activeDot={{ r: 5, stroke: theme.palette.background.paper, strokeWidth: 2 }}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
